import { useContext, useState } from "react";
import { Context } from "../../../context/Context";
import { createCrmUserAPI, resendCrmInvitationAPI } from "../../../utils/api/crm";
import { Button, FileText } from "../../ui";
import "./CRMExpand.css";
import {
  DetailSection,
  FileItem,
} from "../../onboardingpage/StoreOnboardingAdmin/components/Shared/Shared";
import { getQuotationStatusMeta, getStageMeta, getText, getWorkflowStatusMeta } from "../../onboardingpage/onboarding";

const Section = ({ title, children }) => (
  <div className="expand-section">
    <h4>{title}</h4>
    <div className="expand-flex">{children}</div>
  </div>
);
const Field = ({ label, value }) => (
  <div className="expand-field">
    <span className="label">{label}</span>
    <span className="value">{value === 0 ? 0 : value || "-"}</span>
  </div>
);

const STORE_STATUS = {
  ar: { new: "جديد", inProgress: "قيد الإكمال", pendingReview: "بانتظار المراجعة", approved: "تمت الموافقة", rejected: "مرفوض للتعديل" },
  en: { new: "New", inProgress: "In progress", pendingReview: "Pending review", approved: "Approved", rejected: "Returned for changes" },
};

/*
  A CRM lead's detail. The profile comes from the lead's STORE (a client that registered), its PROSPECT profile (entered by staff, no account
  yet) or - for historical leads only - the old onboarding record, shown read-only together with its quotation and receipt so nothing
  historical is hidden. The quotation / payment workflow itself no longer exists here: subscriptions and payments are in Billing.
*/
export default function CRMExpand({ crm, onRefresh }) {
  const { lang, handleTokenExpiration } = useContext(Context);
  const [creatingUser, setCreatingUser] = useState(false);
  const [userMessage, setUserMessage] = useState("");
  const t = getText(lang);
  const profile = crm.onboarding;
  if (!profile?._id)
    return (
      <div className="expand-container">
        <p className="crm-migration-notice">
          This CRM record is preserved, but it has no business profile (its historical record no longer exists). Edit it to add one.
        </p>
      </div>
    );
  const { store = {}, contact = {}, entity = {}, quotation = {}, workFlow = {} } = profile;
  const historical = profile.source === "historical";
  const createUser = async () => {
    const token = localStorage.getItem("auth-token");
    if (!token) return handleTokenExpiration();
    setCreatingUser(true);
    setUserMessage("");
    try {
      const result = await createCrmUserAPI(token, crm._id);
      setUserMessage(
        result.invitationEmail === "failed"
          ? "User created, but the invitation email failed. Use “Resend invitation”."
          : result.message || "User created and linked successfully.",
      );
      await onRefresh?.();
    } catch (error) {
      setUserMessage(error.message);
    } finally {
      setCreatingUser(false);
    }
  };
  const resendInvitation = async () => {
    const token = localStorage.getItem("auth-token");
    if (!token) return handleTokenExpiration();
    setCreatingUser(true);
    setUserMessage("");
    try {
      await resendCrmInvitationAPI(token, crm._id);
      setUserMessage("A new invitation link was sent. The previous link no longer works.");
    } catch (error) {
      setUserMessage(error.message);
    } finally {
      setCreatingUser(false);
    }
  };

  const renderHistoricalDocuments = () => {
    const files = profile.documents?.files || [];
    return (
      <DetailSection title={t.documents}>
        {files.length ? (
          <div className="admin-onboarding-files-grid">
            {files.map((file, index) => (
              <FileItem key={`${file.category}-${index}`} label={file.category || t.file} file={file} onboardingId={profile._id} kind="document" index={index} />
            ))}
          </div>
        ) : (
          <div className="admin-onboarding-empty-card">
            <FileText size={20} />
            <span>{t.noData}</span>
          </div>
        )}
      </DetailSection>
    );
  };

  return (
    <div className="expand-container">
      <div className="expand-grid">
        <Section title="Customer Overview">
          <Field label="Business name" value={store.businessName} />
          <Field label="City" value={store.city} />
          <Field label="Branch count" value={store.branchCount} />
          <Field label="Owner" value={store.ownerName} />
        </Section>
        <Section title="Contact Information">
          <Field label="Contact name" value={contact.contactName} />
          <Field label="Job title" value={contact.jobTitle} />
          <Field label="Mobile" value={contact.mobile} />
          <Field label="Email" value={contact.email || store.email} />
        </Section>
        <Section title="Business / Entity Information">
          <Field label="Entity" value={entity.entityName} />
          <Field label="CR number" value={entity.commercialRegistration} />
          <Field label="VAT number" value={entity.vatNumber} />
          <Field
            label="CR expiration"
            value={
              entity.crExpiration
                ? new Date(entity.crExpiration).toLocaleDateString()
                : "-"
            }
          />
          <Field label={lang === "ar" ? "ملاحظات" : "Notes"} value={profile.documents?.notes} />
        </Section>
        <Section title="CRM Management">
          <Field label="CRM status" value={crm.crm?.status} />
          <Field label="Assigned user" value={crm.crm?.assignedUser?.name} />
          <Field
            label="Follow-up"
            value={
              crm.crm?.followUpEndDate
                ? new Date(crm.crm.followUpEndDate).toLocaleString()
                : "-"
            }
          />
          <Field label="Date status" value={crm.crm?.dateStatus} />
          {profile.user ? (
            <>
              <Field
                label="Client account"
                value={`User Linked${profile.user.email ? ` — ${profile.user.email}` : ""}`}
              />
              <Button
                type="button"
                variant="primary"
                appearance="outline"
                disabled={creatingUser}
                onClick={resendInvitation}
              >
                Resend invitation
              </Button>
            </>
          ) : (
            <Button
              type="button"
              variant="primary"
              appearance="filled"
              disabled={creatingUser || !(contact.email || store.email)}
              onClick={createUser}
            >
              {creatingUser ? "Creating User…" : "Create User"}
            </Button>
          )}
          {!profile.user && !(contact.email || store.email) && (
            <span className="muted">Add a valid contact email before creating a user.</span>
          )}
          {userMessage && <span className="muted">{userMessage}</span>}
        </Section>
        {profile.source === "store" && (
          <Section title="Onboarding Progress">
            <Field label="Store" value={profile.storeCode} />
            <Field label="Onboarding status" value={(STORE_STATUS[lang === "ar" ? "ar" : "en"])[profile.onboardingStatus] || profile.onboardingStatus} />
          </Section>
        )}
        {historical && (
          <Section title={lang === "ar" ? "سجل تاريخي (للقراءة فقط)" : "Historical record (read-only)"}>
            {renderHistoricalDocuments()}
            <Field label="Final price" value={quotation.finalPrice} />
            <Field label="Quotation status" value={getQuotationStatusMeta(quotation.status, lang).label} />
            <FileItem label={t.receipt} file={quotation?.paymentReceipt} onboardingId={profile._id} kind="quotation" />
            <div className={`crm-workflow-status status-${workFlow.status || "draft"}`}>
              {getWorkflowStatusMeta(workFlow.status, lang).label}
            </div>
            <Field label="Current stage" value={getStageMeta(workFlow.currentStage, lang).label} />
            {workFlow.rejectionReason && <Field label="Rejection reason" value={workFlow.rejectionReason} />}
          </Section>
        )}
      </div>
    </div>
  );
}
